import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, Heart, Lock, ChevronDown, ExternalLink } from 'lucide-react';
import { mainNavigation, utilityNav } from '../../data/navigation';

export default function ProfessionalNavbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const toggleLang = () => {
    const next = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(next);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/programs?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setMobileSearchOpen(false);
      setMobileOpen(false);
    }
  };

  return (
    <>
    <div className="flex flex-col w-full z-50 fixed top-0 left-0 right-0">
      {/* Tier 1: Top Utility Bar - NSCC Style */}
      <div className="bg-navy text-white py-2.5 px-4 sm:px-6 lg:px-8 text-[10px] md:text-xs font-semibold border-b border-white/10 shadow-sm relative z-50">
        <div className="max-w-7xl mx-auto flex justify-between sm:justify-end items-center gap-4 md:gap-6">
          <div className="flex items-center gap-4 md:gap-6">
            {utilityNav.quickLinks.map((link) => (
              <div key={link.href} className="flex items-center gap-4">
                <Link to={link.href} className="flex items-center gap-1.5 hover:underline transition-all group">
                  {link.label === 'Donate' && <Heart className="w-3 h-3 text-gold fill-gold group-hover:scale-110 transition-transform" />}
                  {t(`nav_${link.label.toLowerCase()}`, link.label)}
                </Link>
                <span className="opacity-30 last:hidden">|</span>
              </div>
            ))}
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative group">
              <div className="flex items-center gap-1.5 cursor-pointer hover:text-gold transition-colors">
                <Lock className="w-3 h-3 text-gold" /> 
                {t('nav_portal', 'Portail Étudiant')}
                <ChevronDown className="w-3 h-3 group-hover:translate-y-0.5 transition-transform" />
              </div>
              
              {/* Login Tools Dropdown */}
              <div className="absolute top-full right-0 mt-2 w-64 bg-white text-navy shadow-2xl rounded-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-hidden border border-gray-100">
                <div className="p-4 space-y-3 text-left">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4 border-b border-gray-50 pb-2">Student & Staff Portal</p>
                  {utilityNav.loginTools.map((tool) => (
                    <a 
                      key={tool.href} 
                      href={tool.href} 
                      target={tool.external ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="block group/item"
                    >
                      <div className="flex items-center justify-between font-bold text-sm group-hover/item:text-gold transition-colors">
                        {tool.label}
                        {tool.external && <ExternalLink className="w-3 h-3 opacity-50" />}
                      </div>
                      {tool.description && <p className="text-[10px] text-gray-500 font-medium leading-tight mt-0.5">{tool.description}</p>}
                    </a>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Lang Toggle */}
            <button onClick={toggleLang} className="flex items-center gap-1 hover:text-gold transition-colors border-l border-white/20 pl-4 ml-2 uppercase font-black tracking-tighter">
              <span className={i18n.language === 'fr' ? 'text-gold' : ''}>FR</span>
              <span className="opacity-30">/</span>
              <span className={i18n.language === 'en' ? 'text-gold' : ''}>EN</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tier 2: Main Header - NSCC Style */}
      <header className="bg-white text-navy py-4 md:py-6 px-4 sm:px-6 lg:px-8 border-b border-gray-100 shadow-sm relative z-40">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Logo Area */}
          <Link to="/" className="flex items-center gap-4 group">
            <img 
              src="/images/logo/logo-estad.png" 
              alt="ESTAD Logo" 
              className="h-12 md:h-14 w-auto object-contain rounded-full border border-gray-100 p-1 group-hover:border-gold transition-all" 
            />
            <div className="flex flex-col">
              <span className="text-2xl md:text-3xl font-black italic tracking-tighter leading-none text-navy uppercase">ESTAD</span>
              <span className="text-[8px] md:text-[9px] uppercase tracking-[0.3em] font-black mt-1 text-gold">University Institute</span>
            </div>
          </Link>

          {/* Search Bar - Desktop */}
          <div className="hidden lg:flex flex-1 max-w-md mx-12">
            <form onSubmit={handleSearch} className="relative w-full">
              <input 
                type="text" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search programs, courses..."
                className="w-full bg-gray-50 border border-gray-200 px-5 py-3 text-sm font-bold text-navy focus:outline-none focus:border-gold focus:ring-4 focus:ring-gold/5 transition-all rounded-sm"
              />
              <button type="submit" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-all">
                <Search className="w-5 h-5" />
              </button>
            </form>
          </div>

          {/* Nav Actions */}
          <div className="flex items-center gap-4 md:gap-6">
            <button 
              onClick={() => {
                setMobileOpen(!mobileOpen);
                setMobileSearchOpen(false);
              }}
              className={`flex items-center gap-3 border px-6 md:px-10 py-3 transition-all uppercase font-black text-xs md:text-sm tracking-[0.2em] active:scale-95 ${
                mobileOpen ? 'bg-gold border-gold text-white shadow-lg' : 'bg-navy border-navy text-white hover:bg-navy-light'
              }`}
            >
              <span className="hidden sm:inline">Menu</span>
              {mobileOpen ? <X className="w-5 h-5 md:w-6 md:h-6" /> : <Menu className="w-5 h-5 md:w-6 md:h-6" />}
            </button>
            
            <button 
              onClick={() => {
                setMobileSearchOpen(!mobileSearchOpen);
                setMobileOpen(false);
              }}
              className="lg:hidden p-3 hover:bg-gray-100 transition-all rounded-full border border-gray-200"
            >
              {mobileSearchOpen ? <X className="w-6 h-6 text-navy" /> : <Search className="w-6 h-6 text-navy" />}
            </button>
          </div>
        </div>

        {/* Mobile Search Overlay */}
        {mobileSearchOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 p-4 shadow-xl animate-in slide-in-from-top-2 duration-200">
            <form onSubmit={handleSearch} className="relative w-full">
              <input 
                autoFocus
                type="text" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search programs, courses..."
                className="w-full bg-gray-50 border border-gray-200 px-5 py-4 text-sm font-bold text-navy focus:outline-none focus:border-gold transition-all rounded-sm"
              />
              <button type="submit" className="absolute right-4 top-1/2 -translate-y-1/2 text-navy">
                <Search className="w-6 h-6" />
              </button>
            </form>
          </div>
        )}

        {/* Mega Menu Overlay */}
        {mobileOpen && (
          <div className="absolute top-full left-0 right-0 bg-white text-navy border-t border-gray-200 shadow-2xl animate-in slide-in-from-top-4 duration-300 max-h-[85vh] overflow-y-auto overflow-x-hidden">
            <div className="max-w-7xl mx-auto p-8 md:p-16">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-16 md:gap-12">
                {mainNavigation.map((section) => (
                  <div key={section.title} className="space-y-8">
                    <Link 
                      to={section.href} 
                      onClick={() => setMobileOpen(false)}
                      className="block border-b-2 border-gold pb-3 group"
                    >
                      <h4 className="font-black uppercase tracking-[0.3em] text-[10px] text-gold group-hover:translate-x-1 transition-transform">{section.title}</h4>
                    </Link>
                    <div className="space-y-8">
                      {section.items.map((item) => (
                        <div key={item.label} className="space-y-4">
                          <Link 
                            to={item.href} 
                            onClick={() => setMobileOpen(false)}
                            className="text-navy font-black text-sm uppercase tracking-widest hover:text-gold transition-colors block border-l-2 border-transparent hover:border-gold pl-3"
                          >
                            {item.label}
                          </Link>
                          {item.children && (
                            <ul className="space-y-3 pl-6 border-l border-gray-100">
                              {item.children.map((child) => (
                                <li key={child.label}>
                                  <Link 
                                    to={child.href} 
                                    onClick={() => setMobileOpen(false)}
                                    className={`text-xs font-bold transition-all uppercase tracking-wider ${
                                      child.featured ? 'text-gold font-black underline underline-offset-4' : 'text-gray-400 hover:text-navy'
                                    }`}
                                  >
                                    {child.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Bottom Menu Bar */}
              <div className="mt-20 pt-12 border-t border-gray-100 flex flex-wrap justify-between items-center gap-10">
                <div className="space-y-2">
                   <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">Ready to start your journey?</p>
                   <Link 
                    to="/admissions" 
                    onClick={() => setMobileOpen(false)}
                    className="bg-gold text-white font-black py-5 px-16 rounded-sm uppercase tracking-[0.2em] text-sm shadow-xl hover:brightness-110 transition-all inline-block active:scale-95"
                  >
                    {t('hero_cta_apply', 'Apply Now')}
                  </Link>
                </div>
                
                <div className="flex flex-wrap gap-x-12 gap-y-6 text-xs font-black uppercase tracking-[0.2em] text-gray-400">
                  <Link to="/contact" onClick={() => setMobileOpen(false)} className="hover:text-navy transition-colors">Contact Us</Link>
                  <Link to="/campus" onClick={() => setMobileOpen(false)} className="hover:text-navy transition-colors">Campus Life</Link>
                  <Link to="/news" onClick={() => setMobileOpen(false)} className="hover:text-navy transition-colors">Latest News</Link>
                  <Link to="/research" onClick={() => setMobileOpen(false)} className="hover:text-navy transition-colors">Research</Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

    </div>
    {/* Spacer OUTSIDE the fixed wrapper — pushes page content below the fixed navbar */}
    <div className="h-[116px] sm:h-[120px] md:h-[140px] lg:h-[148px] shrink-0" />
    </>
  );
}
